import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import {
  Web,
  CheckCircle,
  Edit,
  Close,
  PhoneIphone,
  DesktopWindows,
  Send,
  ArrowForward,
  Refresh,
} from "@mui/icons-material";
import { useSmbOnboarding } from "../../../contexts/SmbOnboardingContext";
import {
  generateWebsiteVariants,
  selectWebsiteVariant,
  editWebsiteViaChat,
  listWebsiteVariants,
} from "../../../api/smb.api";

const WebsiteStep = ({ onComplete, businessId }) => {
  const { websiteVariants, refreshWebsites } = useSmbOnboarding();
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewMode, setPreviewMode] = useState("mobile");
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (businessId) refreshWebsites(businessId);
  }, [businessId, refreshWebsites]);

  useEffect(() => {
    const selected = websiteVariants.find((v) => v.is_selected === 1);
    if (selected) setSelectedVariant(selected.id);
  }, [websiteVariants]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await generateWebsiteVariants(businessId);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Process SSE events for progress
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
      }
      await refreshWebsites(businessId);
    } catch (err) {
      console.error("Website generation error:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelect = async (websiteId) => {
    await selectWebsiteVariant(businessId, websiteId);
    setSelectedVariant(websiteId);
    await refreshWebsites(businessId);
  };

  const handlePreview = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleEdit = async () => {
    if (!editPrompt.trim() || !selectedVariant) return;
    setIsEditing(true);
    try {
      const response = await editWebsiteViaChat(businessId, selectedVariant, editPrompt);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
      }
      setEditPrompt("");
      await refreshWebsites(businessId);
    } catch (err) {
      console.error("Website edit error:", err);
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Your Business Website
        </Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 3 }}>
          Choose from 6 AI-generated website designs for your business
        </Typography>

        {websiteVariants.length === 0 && !isGenerating && (
          <Button
            variant="contained"
            startIcon={<Web />}
            onClick={handleGenerate}
            fullWidth
            sx={{
              py: 1.5,
              bgcolor: "#059669",
              "&:hover": { bgcolor: "#047857" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Generate Website Options
          </Button>
        )}

        {isGenerating && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#059669", mb: 2 }} />
            <Typography sx={{ color: "#9CA3AF" }}>Creating 6 website variants...</Typography>
          </Box>
        )}

        {websiteVariants.length > 0 && (
          <>
            <Grid container spacing={2}>
              {websiteVariants.map((variant) => (
                <Grid item xs={6} sm={4} key={variant.id}>
                  <Card
                    sx={{
                      bgcolor: "#141626",
                      border: `2px solid ${selectedVariant === variant.id ? "#10B981" : "#282D51"}`,
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "border-color 0.2s",
                      "&:hover": { borderColor: "#3B82F6" },
                    }}
                    onClick={() => handleSelect(variant.id)}
                  >
                    <Box
                      sx={{
                        height: 140,
                        bgcolor: variant.color_override_primary?.String || "#282D51",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <Web sx={{ fontSize: 40, color: "rgba(255,255,255,0.5)" }} />
                      {selectedVariant === variant.id && (
                        <CheckCircle
                          sx={{ position: "absolute", top: 8, right: 8, color: "#10B981", fontSize: 24 }}
                        />
                      )}
                    </Box>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                        Design {variant.variant_index + 1}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 1.5 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreview(variant.website_url?.String);
                        }}
                        sx={{ color: "#3B82F6", textTransform: "none", fontSize: "0.7rem" }}
                      >
                        Preview
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Button
              startIcon={<Refresh />}
              onClick={handleGenerate}
              sx={{ mt: 2, color: "#9CA3AF", textTransform: "none" }}
            >
              Regenerate All
            </Button>
          </>
        )}
      </Paper>

      {/* AI Editor */}
      {selectedVariant && (
        <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Edit with AI
          </Typography>
          <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 2 }}>
            Describe what you'd like to change
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              placeholder="e.g., Make the hero section blue and add our Diwali offer"
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEdit()}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  bgcolor: "#141626",
                  "& fieldset": { borderColor: "#282D51" },
                  "&:hover fieldset": { borderColor: "#3B82F6" },
                  "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleEdit}
              disabled={isEditing || !editPrompt.trim()}
              sx={{ bgcolor: "#3B82F6", "&:hover": { bgcolor: "#2563EB" }, minWidth: 48 }}
            >
              {isEditing ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : <Send />}
            </Button>
          </Box>
        </Paper>
      )}

      <Button
        variant="contained"
        endIcon={<ArrowForward />}
        onClick={onComplete}
        disabled={!selectedVariant}
        fullWidth
        sx={{
          py: 1.5,
          bgcolor: "#3B82F6",
          "&:hover": { bgcolor: "#2563EB" },
          "&.Mui-disabled": { bgcolor: "#282D51", color: "#6B7280" },
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 2,
        }}
      >
        Continue to Calling Agent
      </Button>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1B1D2A", color: "#fff", display: "flex", justifyContent: "space-between" }}>
          Website Preview
          <Box>
            <IconButton onClick={() => setPreviewMode("mobile")} sx={{ color: previewMode === "mobile" ? "#3B82F6" : "#9CA3AF" }}>
              <PhoneIphone />
            </IconButton>
            <IconButton onClick={() => setPreviewMode("desktop")} sx={{ color: previewMode === "desktop" ? "#3B82F6" : "#9CA3AF" }}>
              <DesktopWindows />
            </IconButton>
            <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: "#9CA3AF" }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#101220", p: 0, display: "flex", justifyContent: "center" }}>
          <iframe
            src={previewUrl}
            title="Website Preview"
            style={{
              width: previewMode === "mobile" ? "375px" : "100%",
              height: "80vh",
              border: "none",
              borderRadius: previewMode === "mobile" ? "8px" : 0,
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WebsiteStep;
